import { useState, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { X, Send, Check, RefreshCw, Pencil, Trash2, Plus, Users, Paperclip, CheckCircle2, Circle, Repeat, Archive, ArchiveRestore } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { useTaskActions, useProfiles } from '../../hooks/useTasks'
import { useProjects } from '../../hooks/useProjects'
import { useAuth } from '../../hooks/useAuth'
import { formatDate } from '../../lib/helpers'
import { AssignmentBadge, UrgencyBadge, StatusBadge, PriorityBadge, showToast } from '../ui'
import { SlidePanel, SuccessBurst, ShakeReject } from '../ui/animations'
import ActivityLog from './ActivityLog'
import DeclineModal from './DeclineModal'
import ReassignModal from './ReassignModal'
import DeleteConfirmModal from './DeleteConfirmModal'
import { FilePickerInput, AttachmentList, CommentAttachments, hasOversizedFiles } from '../ui/FileAttachment'
import { useAttachments } from '../../hooks/useAttachments'
import { useTaskAssigneeCompletion } from '../../hooks/useTaskAssigneeCompletion'
import { completionProgress, canForceClose, isAssigneeOpen } from '../../lib/perAssigneeCompletion'
import TaskChatSection from './TaskChatSection'
import SubtasksSection from './SubtasksSection'
import DependenciesSection from './DependenciesSection'
import { URL_RE_SOURCE, URL_RE_FLAGS, normalizeUrlMatch } from '../../lib/linkify'
import { truncateParentLabel } from '../../lib/subtasks'
import { useTaskDependencies } from '../../hooks/useTaskDependencies'
import { getOpenBlockers, shouldWarnOnStatusChange } from '../../lib/dependencies'

// Auto-linkify URLs in plain text segments. Mirrors the URL handling in
// RichContentRenderer so comments get the same behaviour (bare domains,
// www.-prefixed, and full protocol URLs) without pulling in the full rich
// content pipeline (task comments have no stored mentions array, so
// RichContentRenderer would lose the existing @mention highlight).
function linkifyText(text, keyBase) {
  if (!text) return text
  const nodes = []
  let lastIndex = 0
  let match
  let k = 0
  const re = new RegExp(URL_RE_SOURCE, URL_RE_FLAGS)
  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) nodes.push(text.slice(lastIndex, match.index))
    const { displayUrl, href, trailing } = normalizeUrlMatch(match[1])
    nodes.push(
      <a
        key={`${keyBase}-${k++}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-brand-600 dark:text-brand-400 hover:underline break-all"
      >
        {displayUrl}
      </a>
    )
    if (trailing) nodes.push(trailing)
    lastIndex = re.lastIndex
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex))
  return nodes
}

export default function TaskDetailPanel({ task, tasks = [], onClose, onUpdated }) {
  const { profile, isAdmin, isManager } = useAuth()
  const { updateTask, addComment, getTaskComments, acceptTask, declineTask, reassignTask, deleteTask, addAssignee, removeAssignee, archiveTasks, unarchiveTasks } = useTaskActions()
  const { profiles: allProfiles } = useProfiles({ excludeExternals: true })
  const { uploadAttachments, getTaskAttachments, getAttachmentUrl, deleteAttachment } = useAttachments()
  const { markSelfComplete, unmarkSelf, setAssigneeCompletion, forceClose } = useTaskAssigneeCompletion()
  const { blockers: dependencyBlockerRows } = useTaskDependencies(task?.id)
  const { projects: myProjects } = useProjects()

  // Tag this task to a project (turns it into a Feature) or untag it. Placing
  // it in the project's first list keeps it visible on the board.
  async function handleSetProject(projectId) {
    if (projectId === (task.project_id || null)) return  // no-op: same project
    if (!projectId) {
      await updateTask(task.id, { project_id: null, project_column_id: null, project_pos: null })
    } else {
      const { data: cols } = await supabase
        .from('project_columns').select('id').eq('project_id', projectId).order('pos', { ascending: true }).limit(1)
      await updateTask(task.id, { project_id: projectId, project_column_id: cols?.[0]?.id || null, project_pos: 1000 })
    }
    showToast(projectId ? 'Added to project' : 'Removed from project')
    onUpdated?.()
  }

  // Per-assignee completion state (sourced from the raw task_assignees join).
  const completionMap = Object.fromEntries(
    (task?.task_assignees || []).map(r => [r.profile_id, r])
  )
  const { done: doneCount, total: totalCount } = completionProgress(task?.task_assignees)
  const canClose = canForceClose(task, profile?.id, isAdmin)

  const [status,   setStatus]   = useState(task?.status || 'Not Started')
  const [notes,    setNotes]    = useState(task?.notes || '')
  const [comments, setComments] = useState([])
  const [newComment, setNewComment] = useState('')
  const [saving,   setSaving]   = useState(false)
  const [posting,  setPosting]  = useState(false)
  const [loadingComments, setLoadingComments] = useState(true)
  const [mentionedIds, setMentionedIds] = useState([])
  const [mentionQuery, setMentionQuery] = useState(null) // null = picker closed, '' = open no filter
  const [mentionIndex, setMentionIndex] = useState(0)
  const commentRef = useRef(null)
  const [showDeclineModal, setShowDeclineModal] = useState(false)
  const [showReassignModal, setShowReassignModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [acceptAnim, setAcceptAnim] = useState(0)
  const [declineAnim, setDeclineAnim] = useState(0)
  const [editing, setEditing] = useState(false)
  const [editTitle, setEditTitle] = useState('')
  const [editUrgency, setEditUrgency] = useState('')
  const [editDueDate, setEditDueDate] = useState('')
  const [editWhoTo, setEditWhoTo] = useState('')
  const [savingEdit, setSavingEdit] = useState(false)
  const [showAddAssignee, setShowAddAssignee] = useState(false)
  const [attachments, setAttachments] = useState([])
  const [commentFiles, setCommentFiles] = useState([])
  const [commentsOpen, setCommentsOpen] = useState(false)
  const [parentInfo, setParentInfo] = useState(null) // {id, title} when viewing a sub-task

  const location = useLocation()

  // Auto-open the Comments accordion when the URL is a deep-link to a
  // specific comment (?comment=<id>) — otherwise the MyTasksPage poller
  // can't find [data-comment-id] in the DOM and the email-link scroll +
  // highlight silently no-ops. Only fires while ?comment is present;
  // once MyTasksPage strips the param after consuming, this effect
  // doesn't re-toggle, so the user's accordion preference is respected
  // on subsequent re-opens of the same task.
  useEffect(() => {
    if (!task) return
    const targetCommentId = new URLSearchParams(location.search).get('comment')
    if (targetCommentId) setCommentsOpen(true)
  }, [task?.id, location.search])

  const canEdit = isAdmin ||
    (task?.team_id && profile?.team_roles?.[task.team_id] === 'Manager') ||
    task?.assigned_to === profile?.id ||
    task?.assigned_by === profile?.id
  const isOwner = task?.assigned_by === profile?.id || isAdmin

  const isPending = task?.acceptance_status === 'Pending'
  const isDeclined = task?.acceptance_status === 'Declined'
  const isMyTask = task?.assigned_to === profile?.id || task?.task_assignees?.some(ta => ta.profile_id === profile?.id)
  // Any assignee can invite more people onto the task (RLS: migration 117).
  // Removing assignees stays owner-only.
  const canAddAssignee = isOwner || isMyTask
  const canAcceptDecline = isPending && isMyTask
  const canReassign = isDeclined && (task?.assigned_by === profile?.id || isAdmin)

  useEffect(() => {
    if (!task) return
    setStatus(task.status || 'Not Started')
    setNotes(task.notes || '')
    setEditing(false)
    setEditTitle(task.title || '')
    setEditUrgency(task.urgency || 'Med')
    setEditDueDate(task.due_date ? new Date(task.due_date).toISOString().slice(0, 16) : '')
    setEditWhoTo(task.who_due_to || '')
    // Reset per-task composer state so attachments / drafts / mentions
    // don't leak across task switches. commentsOpen is deliberately NOT
    // reset — the accordion state is a session-level preference.
    setCommentFiles([])
    setMentionedIds([])
    setMentionQuery(null)
    setMentionIndex(0)
    setNewComment('')
    // Resolve parent breadcrumb if this is a sub-task. Single-row select; the
    // parent title is enough for the pill. Cleared first to avoid showing the
    // previous task's parent during a panel switch.
    setParentInfo(null)
    if (task.parent_task_id) {
      supabase
        .from('tasks')
        .select('id, title')
        .eq('id', task.parent_task_id)
        .maybeSingle()
        .then(({ data }) => { if (data) setParentInfo(data) })
    }
    setLoadingComments(true)
    getTaskComments(task.id).then(data => {
      setComments(data)
      setLoadingComments(false)
    })
    getTaskAttachments(task.id).then(res => {
      if (res.ok) setAttachments(res.attachments)
    })
  }, [task?.id])

  // Auto-scroll the panel to the Chat section on open when the viewer has
  // unread messages in this task's chat. We query conversation_participants
  // to compare last_read_at vs. the conversation's last_message_at; scroll
  // only when strictly behind.
  useEffect(() => {
    if (!task?.id || !profile?.id) return
    let cancelled = false
    let timerId = null
    ;(async () => {
      const { data: conv } = await supabase
        .from('conversations')
        .select('id, last_message_at')
        .eq('kind', 'task')
        .eq('task_id', task.id)
        .maybeSingle()
      if (cancelled || !conv?.last_message_at) return
      const { data: part } = await supabase
        .from('conversation_participants')
        .select('last_read_at')
        .eq('conversation_id', conv.id)
        .eq('user_id', profile.id)
        .maybeSingle()
      if (cancelled || !part) return
      const hasUnread = new Date(part.last_read_at || 0) < new Date(conv.last_message_at)
      if (hasUnread) {
        timerId = setTimeout(() => {
          document.getElementById('task-chat-section')
            ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        }, 200)
      }
    })()
    return () => {
      cancelled = true
      if (timerId) clearTimeout(timerId)
    }
  }, [task?.id, profile?.id])

  function startEditing() {
    setEditTitle(task.title || '')
    setEditUrgency(task.urgency || 'Med')
    setEditDueDate(task.due_date ? new Date(task.due_date).toISOString().slice(0, 16) : '')
    setEditWhoTo(task.who_due_to || '')
    setEditing(true)
  }

  async function handleSaveEdit() {
    if (editDueDate && new Date(editDueDate) < new Date()) {
      showToast('Due date must be in the future', 'error')
      return
    }
    setSavingEdit(true)
    const updates = {
      title: editTitle.trim(),
      urgency: editUrgency,
      due_date: editDueDate || null,
      who_due_to: editWhoTo.trim() || null,
    }
    const result = await updateTask(task.id, updates)
    setSavingEdit(false)
    if (result.ok) {
      showToast('Task details updated')
      setEditing(false)
      onUpdated?.()
    } else {
      showToast(result.msg, 'error')
    }
  }

  async function handleSave() {
    if (!task) return
    // Soft dependency warning: surface a toast (does NOT block) when the
    // user moves a blocked task into an active state. v1 is display-only.
    const taskById = new Map((tasks || []).map(t => [t.id, t]))
    const openBlockers = getOpenBlockers(dependencyBlockerRows, taskById)
    if (shouldWarnOnStatusChange(task.status, status, openBlockers.length)) {
      const names = openBlockers.slice(0, 2).map(b => b.title).join(', ')
      const more = openBlockers.length > 2 ? ` +${openBlockers.length - 2} more` : ''
      showToast(`Heads up — still blocked by: ${names}${more}`, 'error')
    }
    setSaving(true)
    const result = await updateTask(task.id, { status, notes })
    setSaving(false)
    if (result.ok) {
      showToast('Task updated')
      onUpdated?.()
    } else {
      showToast(result.msg, 'error')
    }
  }

  async function handleAccept() {
    const result = await acceptTask(task.id)
    if (result.ok) {
      setAcceptAnim(prev => prev + 1)
      showToast('Task accepted')
      onUpdated?.()
    } else {
      showToast(result.msg, 'error')
    }
  }

  async function handleDecline(reason) {
    const result = await declineTask(task.id, reason)
    if (result.ok) {
      setDeclineAnim(prev => prev + 1)
      showToast('Task declined')
      onUpdated?.()
    } else {
      showToast(result.msg, 'error')
    }
  }

  async function handleDelete() {
    const result = await deleteTask(task.id)
    if (result.ok) {
      showToast('Task deleted')
      onClose()
      onUpdated?.()
    } else {
      showToast(result.msg, 'error')
    }
  }

  async function handleArchiveToggle() {
    // Archive is personal (migration 105) — any viewer can file the task away
    // for themselves, so this isn't gated behind edit permissions. onUpdated
    // closes the panel + refetches, so the row leaves whichever list it was in.
    const result = task.archived
      ? await unarchiveTasks([task.id])
      : await archiveTasks([task.id])
    if (result.ok) {
      showToast(task.archived ? 'Task restored' : 'Task archived')
      onUpdated?.()
    } else {
      showToast(result.msg, 'error')
    }
  }

  async function handleAddAssignee(profileId) {
    const result = await addAssignee(task.id, profileId)
    if (result.ok) {
      showToast('Assignee added')
      setShowAddAssignee(false)
      onUpdated?.()
    } else {
      showToast(result.msg, 'error')
    }
  }

  async function handleRemoveAssignee(profileId) {
    const result = await removeAssignee(task.id, profileId)
    if (result.ok) {
      showToast('Assignee removed')
      onUpdated?.()
    } else {
      showToast(result.msg, 'error')
    }
  }

  async function handleReassign(newAssigneeId) {
    const result = await reassignTask(task.id, newAssigneeId)
    if (result.ok) {
      showToast('Task reassigned')
      onUpdated?.()
    } else {
      showToast(result.msg, 'error')
    }
  }

  // Mention picker logic
  const mentionResults = mentionQuery !== null
    ? allProfiles
        .filter(p => p.id !== profile?.id && p.full_name?.toLowerCase().includes(mentionQuery.toLowerCase()))
        .slice(0, 6)
    : []

  function handleCommentChange(e) {
    const val = e.target.value
    setNewComment(val)

    // Detect @ trigger: find the last @ before cursor
    const cursor = e.target.selectionStart
    const textBefore = val.slice(0, cursor)
    const atMatch = textBefore.match(/@(\w*)$/)
    if (atMatch) {
      setMentionQuery(atMatch[1])
      setMentionIndex(0)
    } else {
      setMentionQuery(null)
    }
  }

  function insertMention(p) {
    const textarea = commentRef.current
    const cursor = textarea.selectionStart
    const textBefore = newComment.slice(0, cursor)
    const textAfter = newComment.slice(cursor)
    // Replace the @query with @Name
    const replaced = textBefore.replace(/@\w*$/, `@${p.full_name} `)
    setNewComment(replaced + textAfter)
    setMentionedIds(prev => prev.includes(p.id) ? prev : [...prev, p.id])
    setMentionQuery(null)
    // Refocus
    setTimeout(() => {
      textarea.focus()
      const pos = replaced.length
      textarea.setSelectionRange(pos, pos)
    }, 0)
  }

  function handleCommentKeyDown(e) {
    if (mentionQuery !== null && mentionResults.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setMentionIndex(i => Math.min(i + 1, mentionResults.length - 1))
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setMentionIndex(i => Math.max(i - 1, 0))
        return
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        insertMention(mentionResults[mentionIndex])
        return
      }
      if (e.key === 'Escape') {
        setMentionQuery(null)
        return
      }
    }
    if (e.key === 'Enter' && e.metaKey) handlePostComment()
  }

  async function handlePostComment() {
    if ((!newComment.trim() && commentFiles.length === 0) || !task) return
    if (hasOversizedFiles(commentFiles)) {
      showToast('Remove files over 5 MB before submitting', 'error')
      return
    }
    const text = newComment.trim()
    setPosting(true)
    const result = text
      ? await addComment(task.id, text, mentionedIds)
      : { ok: true, comment: null }
    setPosting(false)
    if (result.ok) {
      if (result.comment) {
        setComments(prev => [result.comment, ...prev])
      }
      // Upload attached files
      if (commentFiles.length > 0) {
        const upload = await uploadAttachments(task.id, commentFiles, result.comment?.id || null)
        if (!upload.ok) {
          showToast('Some files failed to upload', 'error')
        }
        // Refresh attachments list
        const refreshed = await getTaskAttachments(task.id)
        if (refreshed.ok) setAttachments(refreshed.attachments)
        setCommentFiles([])
      }
      setNewComment('')
      setMentionedIds([])
      setMentionQuery(null)

      // Notify assignee, assigner, and @mentioned people (non-blocking)
      if (text) {
        supabase.functions.invoke('user-notify', {
          body: { type: 'comment', taskId: task.id, authorId: profile.id, commentText: text, mentionedIds }
        }).then(({ error }) => {
          if (error) console.warn('Comment notification email failed:', error)
        }).catch(err => console.warn('Comment notification email failed:', err))
      }
    } else {
      showToast(result.msg, 'error')
    }
  }

  if (!task) return null

  return (
    <SlidePanel isOpen={!!task} onClose={onClose}>
      {/* Header */}
      <div className="px-5 py-4 border-b border-slate-100 dark:border-dark-border flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            {parentInfo && (
              <button
                type="button"
                onClick={() => window.dispatchEvent(new CustomEvent('open-task', { detail: { taskId: parentInfo.id } }))}
                className="badge bg-slate-100 hover:bg-slate-200 text-slate-600 dark:bg-dark-hover dark:text-slate-300 text-[10px] inline-flex items-center gap-1 transition-colors"
                title={`Open parent: ${parentInfo.title}`}
                aria-label={`Open parent task ${parentInfo.title}`}
              >
                ↳ {truncateParentLabel(parentInfo.title)}
              </button>
            )}
            {task.recurrence_id && (
              <span
                className="badge bg-purple-500/15 text-purple-700 dark:text-purple-300 text-[10px] inline-flex items-center gap-1"
                title="Spawned from a recurring template — manage in Settings → Recurring Tasks"
              >
                <Repeat size={10} aria-hidden="true" /> Recurring
              </span>
            )}
            {isPending && <span className="badge bg-yellow-500/15 text-yellow-700 text-[10px]">Pending</span>}
            {isDeclined && <span className="badge bg-red-500/15 text-red-700 text-[10px]">Declined</span>}
          </div>
          {editing ? (
            <input
              value={editTitle}
              onChange={e => setEditTitle(e.target.value)}
              className="form-input font-semibold text-slate-900 dark:text-white w-full"
              autoFocus
            />
          ) : (
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-slate-900 dark:text-white leading-snug">{task.title}</h3>
              {isOwner && !editing && (
                <button
                  onClick={startEditing}
                  className="text-slate-300 hover:text-brand-500 dark:text-slate-600 dark:hover:text-brand-400 transition-colors"
                  title="Edit task"
                  aria-label="Edit task"
                >
                  <Pencil size={14} />
                </button>
              )}
            </div>
          )}
        </div>
        <button
          onClick={handleArchiveToggle}
          className="text-slate-400 hover:text-brand-500 dark:text-slate-500 dark:hover:text-brand-400 p-1.5 rounded-xl hover:bg-slate-100 dark:hover:bg-dark-hover transition-all duration-200 flex-shrink-0"
          title={task.archived ? 'Unarchive task' : 'Archive task'}
          aria-label={task.archived ? 'Unarchive task' : 'Archive task'}
        >
          {task.archived ? <ArchiveRestore size={18} /> : <Archive size={18} />}
        </button>
        {canEdit && (
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="text-slate-400 hover:text-red-500 dark:text-slate-500 dark:hover:text-red-400 p-1.5 rounded-xl hover:bg-red-50 dark:hover:bg-red-500/10 transition-all duration-200 flex-shrink-0"
            title="Delete task"
            aria-label="Delete task"
          >
            <Trash2 size={18} />
          </button>
        )}
        <button
          onClick={onClose}
          className="text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 p-1.5 rounded-xl hover:bg-slate-100 dark:hover:bg-dark-hover transition-all duration-200 flex-shrink-0"
          aria-label="Close task panel"
        >
          <X size={20} />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">

        {/* Project link — tag this task to a project (makes it a Feature on the
            project board). Only projects the user is a member of are listed. */}
        {myProjects.length > 0 && (
          <div className="px-5 py-2.5 border-b border-slate-100 dark:border-dark-border flex items-center gap-2">
            <span className="text-xs font-medium text-slate-500 dark:text-slate-400 shrink-0">Project</span>
            <select
              value={task.project_id || ''}
              onChange={e => handleSetProject(e.target.value || null)}
              className="form-input text-xs py-1 px-2 w-auto"
            >
              <option value="">— None —</option>
              {myProjects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
        )}

        {/* Acceptance actions */}
        {canAcceptDecline && (
          <div className="px-5 py-4 border-b border-slate-100 dark:border-dark-border bg-yellow-500/5">
            <p className="text-xs font-semibold text-yellow-700 uppercase tracking-wider mb-3">This task requires your acceptance</p>
            <div className="flex gap-2">
              <SuccessBurst trigger={acceptAnim}>
                <motion.button
                  onClick={handleAccept}
                  className="btn-primary bg-emerald-500 hover:bg-emerald-600 shadow-emerald-500/20"
                  whileTap={{ scale: 0.97 }}
                >
                  <Check size={16} /> Accept
                </motion.button>
              </SuccessBurst>
              <ShakeReject trigger={declineAnim}>
                <motion.button
                  onClick={() => setShowDeclineModal(true)}
                  className="btn-danger"
                  whileTap={{ scale: 0.97 }}
                >
                  <X size={16} /> Decline
                </motion.button>
              </ShakeReject>
            </div>
          </div>
        )}

        {/* Declined info + reassign */}
        {isDeclined && (
          <div className="px-5 py-4 border-b border-slate-100 dark:border-dark-border bg-red-500/5">
            <p className="text-xs font-semibold text-red-700 uppercase tracking-wider mb-2">Task Declined</p>
            {task.decline_reason && (
              <p className="text-sm text-slate-700 dark:text-slate-200 mb-2 italic">"{task.decline_reason}"</p>
            )}
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
              Declined by {task.assignee?.full_name}
              {task.declined_at && <> on {formatDate(task.declined_at)}</>}
            </p>
            {canReassign && (
              <motion.button
                onClick={() => setShowReassignModal(true)}
                className="btn-primary"
                whileTap={{ scale: 0.97 }}
              >
                <RefreshCw size={14} /> Reassign Task
              </motion.button>
            )}
          </div>
        )}

        {/* Edit save/cancel bar */}
        {editing && (
          <div className="px-5 py-3 border-b border-slate-100 dark:border-dark-border bg-brand-50 dark:bg-brand-500/5 flex items-center gap-2">
            <button onClick={handleSaveEdit} disabled={savingEdit} className="btn-primary text-xs px-4 py-1.5">
              {savingEdit ? 'Saving...' : 'Save Changes'}
            </button>
            <button onClick={() => setEditing(false)} className="btn-ghost text-xs px-3 py-1.5">Cancel</button>
          </div>
        )}

        {/* Meta grid */}
        <div className="px-4 sm:px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-3 border-b border-slate-100 dark:border-dark-border">
          {[
            { label: 'Assigned To',   value: (
              <div className="flex flex-wrap items-center gap-1.5">
                {(task.assignees?.length > 0 ? task.assignees : [{ id: task.assigned_to, full_name: task.assignee?.full_name, is_primary: true }]).map(a => {
                  const row = completionMap[a.id]
                  const open = row ? isAssigneeOpen(row) : true
                  const isMe = a.id === profile?.id
                  const canToggleOther = isAdmin || task.assigned_by === profile?.id
                  const canToggle = !!row && (isMe || canToggleOther)
                  return (
                    <span key={a.id} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-medium
                      ${a.is_primary
                        ? 'bg-brand-50 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300'
                        : 'bg-slate-100 text-slate-600 dark:bg-dark-hover dark:text-slate-300'
                      }`}>
                      <button
                        type="button"
                        disabled={!canToggle}
                        onClick={async () => {
                          if (!row) return
                          let res
                          if (isMe) {
                            res = open ? await markSelfComplete(task.id) : await unmarkSelf(task.id)
                          } else {
                            res = await setAssigneeCompletion(task.id, a.id, open)
                          }
                          if (res?.error) {
                            showToast(res.error.message || 'Failed to update', 'error')
                          }
                          // Realtime subscription on task_assignees refetches automatically;
                          // don't call onUpdated() here because parent handlers close the panel.
                        }}
                        className="disabled:opacity-40 disabled:cursor-not-allowed flex items-center"
                        title={canToggle ? (open ? 'Mark done' : 'Unmark done') : (row ? 'Only self, assigner, or admin can toggle' : 'Completion state loading…')}
                        aria-label={open ? `Mark ${a.full_name || 'assignee'} done` : `Unmark ${a.full_name || 'assignee'}`}
                        aria-pressed={!open}
                      >
                        {open
                          ? <Circle size={16} />
                          : <CheckCircle2 size={16} className="text-emerald-600 dark:text-emerald-400" />}
                      </button>
                      <span className={open ? '' : 'line-through opacity-70'}>
                        {a.full_name || 'Unknown'}
                      </span>
                      {!a.is_primary && isOwner && (
                        <button
                          onClick={() => handleRemoveAssignee(a.id)}
                          className="text-slate-400 hover:text-red-500 dark:text-slate-500 dark:hover:text-red-400"
                          aria-label={`Remove ${a.full_name || 'assignee'} from task`}
                        >
                          <X size={10} />
                        </button>
                      )}
                    </span>
                  )
                })}
                {canAddAssignee && (
                  showAddAssignee ? (
                    <select
                      autoFocus
                      className="form-input text-xs py-0.5 px-2 w-auto min-w-[120px]"
                      value=""
                      onChange={e => { if (e.target.value) handleAddAssignee(e.target.value) }}
                      onBlur={() => setShowAddAssignee(false)}
                    >
                      <option value="">Select...</option>
                      {allProfiles
                        .filter(p => !task.assignees?.some(a => a.id === p.id) && p.id !== task.assigned_to)
                        .map(p => (
                          <option key={p.id} value={p.id}>{p.full_name}</option>
                        ))
                      }
                    </select>
                  ) : (
                    <button
                      onClick={() => setShowAddAssignee(true)}
                      className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-lg text-[10px] font-medium text-slate-400 hover:text-brand-500 hover:bg-brand-50 dark:hover:bg-brand-500/10 transition-colors"
                    >
                      <Plus size={10} /> Add
                    </button>
                  )
                )}
                {totalCount > 1 && doneCount < totalCount && canClose && (
                  <button
                    type="button"
                    onClick={async () => {
                      if (!confirm(`Close this task for ${totalCount - doneCount} remaining assignee(s)?`)) return
                      const { error } = await forceClose(task.id)
                      if (error) {
                        showToast(error.message || 'Failed to close task', 'error')
                      } else {
                        showToast('Task closed for everyone')
                        // Realtime subscription refetches; don't call onUpdated()
                        // here because parent handlers close the panel.
                      }
                    }}
                    className="btn btn-secondary text-[10px] py-0.5 px-2"
                    title="Mark this task done for all remaining assignees"
                  >
                    Close for everyone ({doneCount}/{totalCount} done)
                  </button>
                )}
              </div>
            )},
            { label: 'Assigned By',   value: task.assigner?.full_name },
            { label: 'Date Assigned', value: formatDate(task.date_assigned) },
            { label: 'Due Date',      value: editing
              ? <input type="datetime-local" value={editDueDate} onChange={e => setEditDueDate(e.target.value)} min={new Date().toISOString().slice(0, 16)} className="form-input text-sm py-1" />
              : task.due_date ? formatDate(task.due_date) : '—'
            },
            { label: 'Team',          value: task.team?.name },
            { label: 'Reports To',   value: task.assignee?.manager?.full_name || '—' },
            { label: 'For',           value: editing
              ? <input value={editWhoTo} onChange={e => setEditWhoTo(e.target.value)} className="form-input text-sm py-1" placeholder="Who it's for..." />
              : task.who_due_to || '—'
            },
            { label: 'Last Updated',  value: formatDate(task.last_updated) },
            { label: 'Priority',      value: <PriorityBadge priority={task.priority} /> },
          ].map(({ label, value }) => (
            <div key={label}>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">{label}</p>
              <div className="text-sm text-slate-800 dark:text-slate-200">{value}</div>
            </div>
          ))}
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Urgency</p>
            {editing
              ? <select value={editUrgency} onChange={e => setEditUrgency(e.target.value)} className="form-input text-sm py-1">
                  <option>High</option>
                  <option>Med</option>
                  <option>Low</option>
                </select>
              : <UrgencyBadge urgency={task.urgency} />
            }
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Assignment Type</p>
            <AssignmentBadge type={task.assignment_type} />
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Acceptance</p>
            {task.acceptance_status === 'Accepted' && task.assignment_type === 'Superior' ? (
              <span className="text-xs text-slate-400 dark:text-slate-500">Auto-accepted — Superior assigned</span>
            ) : task.acceptance_status === 'Accepted' && task.assignment_type === 'Self' ? (
              <span className="text-xs text-slate-400 dark:text-slate-500">Auto-accepted — Self assigned</span>
            ) : (
              <span className={`badge ${
                task.acceptance_status === 'Accepted' ? 'bg-emerald-500/15 text-emerald-700' :
                task.acceptance_status === 'Pending' ? 'bg-yellow-500/15 text-yellow-700' :
                'bg-red-500/15 text-red-700'
              }`}>
                {task.acceptance_status}
              </span>
            )}
          </div>
        </div>

        {/* Attachments */}
        {attachments.length > 0 && (
          <div className="px-5 py-4 border-b border-slate-100 dark:border-dark-border">
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <Paperclip size={12} />
              Attachments ({attachments.length})
            </p>
            <AttachmentList
              attachments={attachments}
              onDownload={getAttachmentUrl}
              onDelete={async (id, path) => {
                const res = await deleteAttachment(id, path)
                if (res.ok) setAttachments(prev => prev.filter(a => a.id !== id))
                return res
              }}
              currentUserId={profile?.id}
              isAdmin={isAdmin}
            />
          </div>
        )}

        {/* Status + notes update */}
        {canEdit && task.acceptance_status !== 'Declined' && (
          <div className="px-5 py-4 border-b border-slate-100 dark:border-dark-border bg-slate-50 dark:bg-dark-bg">
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">Update Task</p>
            <div className="flex gap-2 items-center mb-3">
              <select
                value={status}
                onChange={e => setStatus(e.target.value)}
                className="form-input flex-1"
              >
                <option>Not Started</option>
                <option>In Progress</option>
                <option>Blocked</option>
                <option>Done</option>
              </select>
              <button
                onClick={handleSave}
                disabled={saving}
                className="btn-primary whitespace-nowrap"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Add or update notes..."
              rows={3}
              className="form-input resize-y min-h-[88px] max-h-[60vh]"
            />
          </div>
        )}

        {/* Sub-tasks (only on parent tasks; component itself bails on children) */}
        {task?.id && (
          <SubtasksSection
            task={task}
            tasks={tasks}
            onOpenChild={(child) => window.dispatchEvent(new CustomEvent('open-task', { detail: { taskId: child.id } }))}
          />
        )}

        {/* Dependencies — soft Blocked-by / Blocks chips */}
        {task?.id && <DependenciesSection task={task} tasks={tasks} />}

        {/* Chat (primary) */}
        {task?.id && <TaskChatSection key={task.id} taskId={task.id} />}

        {/* Comments (accordion — collapsed by default) */}
        <div className="border-t border-slate-100 dark:border-dark-border">
          <button
            type="button"
            onClick={() => setCommentsOpen(v => !v)}
            className="w-full flex items-center justify-between px-4 sm:px-5 py-3 text-left hover:bg-slate-50 dark:hover:bg-dark-hover"
          >
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              {commentsOpen ? '▼' : '▶'} Comments &amp; posts{comments.length > 0 ? ` (${comments.length})` : ''}
            </span>
          </button>
          {commentsOpen && (
        <div className="px-5 pb-4">
          <div className="flex gap-2 items-end mb-4 relative">
            <div className="flex-1 relative">
              <textarea
                ref={commentRef}
                value={newComment}
                onChange={handleCommentChange}
                placeholder="Write a comment... Use @ to mention someone"
                rows={2}
                className="form-input flex-1 w-full resize-none"
                onKeyDown={handleCommentKeyDown}
              />
              {/* @mention dropdown */}
              {mentionQuery !== null && mentionResults.length > 0 && (
                <div className="absolute bottom-full left-0 mb-1 w-full bg-white dark:bg-dark-card rounded-xl shadow-elevated border border-slate-200 dark:border-dark-border z-50 overflow-hidden max-h-48 overflow-y-auto">
                  {mentionResults.map((p, i) => (
                    <button
                      key={p.id}
                      className={`w-full text-left px-3 py-2 flex items-center gap-2 text-sm transition-colors ${
                        i === mentionIndex
                          ? 'bg-brand-50 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300'
                          : 'hover:bg-slate-50 dark:hover:bg-dark-hover text-slate-700 dark:text-slate-200'
                      }`}
                      onMouseDown={e => { e.preventDefault(); insertMention(p) }}
                    >
                      {p.avatar_url
                        ? <img src={p.avatar_url} className="w-5 h-5 rounded-full" alt="" />
                        : <div className="w-5 h-5 rounded-full bg-brand-500 flex items-center justify-center text-white text-[10px] font-bold">
                            {p.full_name?.[0] || '?'}
                          </div>
                      }
                      <span className="font-medium">{p.full_name}</span>
                      <span className="text-xs text-slate-400 dark:text-slate-500">{p.role}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={handlePostComment}
              disabled={posting || (!newComment.trim() && commentFiles.length === 0) || hasOversizedFiles(commentFiles)}
              className="btn-primary h-[60px] px-4"
            >
              <Send size={16} />
            </button>
          </div>
          <div className="mb-4 -mt-2">
            <FilePickerInput files={commentFiles} onChange={setCommentFiles} compact />
          </div>

          {loadingComments ? (
            <p className="text-sm text-slate-400 text-center py-4">Loading comments...</p>
          ) : comments.length === 0 ? (
            <p className="text-sm text-slate-400 italic">No comments yet.</p>
          ) : (
            <div className="space-y-3">
              {comments.map(c => (
                <div key={c.id} data-comment-id={c.id} className="bg-slate-50 dark:bg-dark-bg rounded-xl p-3 border border-slate-100 dark:border-dark-border">
                  <div className="flex items-center gap-2 mb-1.5">
                    {c.author?.avatar_url
                      ? <img src={c.author.avatar_url} className="w-5 h-5 rounded-full" alt="" />
                      : <div className="w-5 h-5 rounded-full bg-brand-500 flex items-center justify-center text-white text-[10px] font-bold">
                          {c.author?.full_name?.[0] || '?'}
                        </div>
                    }
                    <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">{c.author?.full_name}</span>
                    <span className="text-xs text-slate-400 dark:text-slate-500">{formatDate(c.created_at)}</span>
                  </div>
                  <p className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed whitespace-pre-wrap break-words">
                    {c.content.split(/(@\w[\w\s]*?\w)(?=\s|$|[.,!?])/).map((part, i) =>
                      part.startsWith('@')
                        ? <span key={i} className="text-brand-600 dark:text-brand-400 font-semibold">{part}</span>
                        : <span key={i}>{linkifyText(part, `c${c.id}-${i}`)}</span>
                    )}
                  </p>
                  <CommentAttachments
                    attachments={attachments.filter(a => a.comment_id === c.id)}
                    onDownload={getAttachmentUrl}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
          )}
        </div>

        {/* Activity Log */}
        <ActivityLog taskId={task.id} />
      </div>

      {/* Modals */}
      <DeclineModal
        isOpen={showDeclineModal}
        onClose={() => setShowDeclineModal(false)}
        onConfirm={handleDecline}
        taskTitle={task.title}
      />
      <ReassignModal
        isOpen={showReassignModal}
        onClose={() => setShowReassignModal(false)}
        onConfirm={handleReassign}
        task={task}
      />
      <DeleteConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        taskTitle={task.title}
      />
    </SlidePanel>
  )
}
